import React from 'react';
import { AppChrome } from '../alt/AppChrome';
import { ChatsScreen, WorkflowsScreen, PulsesScreen } from '../alt/PlatformPillar';
import { ConnectionsScreen } from '../alt/ConnectedAppsPillar';
import { SlackPanel } from '../SlackIntegrationGraph';
import { SkillShelf } from './SkillShelf';

/**
 * THE CREATOR PACK — the same product, composed for vertical video.
 *
 * A short, opinionated set rather than another full library. Someone cutting a
 * 30-second clip does not need twenty frames; they need the handful that carry
 * a story: what Giga is plugged into, it answering where the team already
 * talks, it answering in its own app, work running on a schedule, and the
 * library of methods behind all of it.
 *
 * Ordered as that story rather than by product area, so the pack cuts in
 * sequence straight down the page.
 *
 * Every plate is a STILL. Nothing here is interactive and nothing animates in
 * — an asset that has to be scrolled into view or clicked to look right is an
 * asset that exports wrong.
 */
export type CreatorAsset = {
  id: string;
  name: string;
  note: string;
  use: string;
  /** Natural width of the screen inside, for the plate's scale maths. */
  width: number;
  /**
   * Width the screen is rendered at ON the plate — the same in both ratios, so
   * switching shape changes the surrounding space and never the zoom. Only set
   * this when a screen needs to run wider than the default 508px.
   */
  renderWidth?: number;
  render: () => React.ReactNode;
};
export const CREATOR_ASSETS: CreatorAsset[] = [{
  id: 'creator-connections',
  name: 'Integrations',
  note: 'Every tool, and the account it runs as',
  use: 'The opening shot for "it works with what you already use". The densest screen in the pack — hold it a beat longer than the others, and prefer 16:9 where its own layout has room.',
  width: 1240,
  // Widest natural screen here, so it runs to the artboard's edges rather than
  // the shared default — below this its account labels stop reading.
  renderWidth: 512,
  render: () => <AppChrome sidebar={false} data-id="element-3241" data-mp="1wtttv64jco69">
      <ConnectionsScreen data-id="element-3242" data-mp="21s525cf6mqmd" />
    </AppChrome>
}, {
  id: 'creator-slack',
  name: 'Slack thread',
  note: 'Someone asks in #sales, Giga answers',
  use: 'The most legible plate in the pack — a message column reads well narrow. Use it for the "no new app to learn" beat.',
  width: 900,
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-3243" data-mp="1e2k89yhhq8yb" />
}, {
  id: 'creator-chat',
  name: 'Giga Chat',
  note: 'An answer with its sources named',
  use: 'The product itself. Use it straight after the Slack plate to show the same answer has a home of its own, with the records it came from.',
  width: 900,
  render: () => <AppChrome sidebar={false} data-id="element-3244" data-mp="195k1tmgqb46o">
      <ChatsScreen data-id="element-3245" data-mp="2yinkg0atx77w" />
    </AppChrome>
}, {
  id: 'creator-workflows',
  name: 'Workflows',
  note: 'Steps, and the runs behind them',
  use: 'For the "it keeps working" beat. The run history is the part worth pausing on — it is the evidence, not the steps.',
  width: 900,
  // Padded the same way the landscape frames pad it — these two screens do
  // not carry their own inner gutter.
  render: () => <AppChrome sidebar={false} data-id="element-3246" data-mp="11jon808t122k">
      <div className="px-7 py-6" data-id="element-3247" data-mp="11kzyel4kxc3p">
        <WorkflowsScreen data-id="element-3248" data-mp="11m1ex54d6qst" />
      </div>
    </AppChrome>
}, {
  id: 'creator-pulses',
  name: 'Pulses',
  note: 'Four on a schedule',
  use: 'The shortest plate to read in the pack — four rows and a time each. Good for a fast cut, or as the closing card under a voiceover.',
  width: 900,
  render: () => <AppChrome sidebar={false} data-id="element-3249" data-mp="16l489sazf8ky">
      <div className="px-7 py-6" data-id="element-3250" data-mp="1kikeb2pd90c6">
        <PulsesScreen data-id="element-3251" data-mp="1khhq52jfhbzy" />
      </div>
    </AppChrome>
}, {
  id: 'creator-skills',
  name: 'Skills library',
  note: 'A shelf running off both edges',
  use: 'The library, cropped rather than contained — the shelf runs off both sides so it reads as bigger than the plate. Use it for the "your company\u2019s way of working, written down" beat.',
  width: 900,
  // Deliberately past the artboard edge: this one is a band, and the whole
  // point is that it continues beyond the frame. The square crops it.
  renderWidth: 580,
  render: () => <SkillShelf columns={4} data-id="element-3252" data-mp="3ntfkhw3vq19r" />
}];